import { ExpandMore } from '@mui/icons-material';
import { Accordion, AccordionDetails, Typography, useEventCallback } from '@mui/material';
import { useFormik, useFormikContext } from 'formik';
import { PmiButton } from 'shell/components/shared/button';
import { Fragment, memo, useEffect, useRef, useState } from 'react';
import { AccordionSummary } from 'shell/components/shared/summary-accordion';
import AccordionIssuedAssessmentForm, { VoidVerificationMessiVM, schema } from './form';
import { useHttpClient } from 'shell/hooks/use-http-fetch/stateless';
import { __PRACTICE_API_URL__ } from 'shell/constants/environment';
import { ResponsePromise } from 'shell/hooks/use-http-fetch/stateless/type';
import { toast } from 'react-toastify';
import { useParams } from 'react-router-dom';
import { PracticeDetailVM } from 'practice/view-models/practice-detail';
import { DateTime } from 'luxon';
import { PracticeData } from 'shell/models/practice';

interface VoidVerificationMessiProps {
  loadUpdateDate: () => Promise<PracticeDetailVM> | void;
  loadViewMessi: () => Promise<void>;
  isUnavailability?: boolean;
  noteMesso?: string;
  datiMesso?: PracticeData;
}

const VoidVerificationMessiComponent = ({
  loadUpdateDate,
  loadViewMessi,
}: VoidVerificationMessiProps): React.ReactElement => {
  const params = useParams();

  const [open, setOpen] = useState(true);

  const ref = useRef<HTMLDivElement | null>(null);

  const formContextPage = useFormikContext<PracticeDetailVM>();

  const insertData = useHttpClient(`${__PRACTICE_API_URL__}/accertamento-messi/annullo`, {
    options: {
      method: 'POST',
      errorCatching: true,
      loading: true,
    },
  });

  useEffect(() => {
    ref.current?.scrollIntoView();
  }, []);

  const onSubmit = useEventCallback((values: VoidVerificationMessiVM) => {
    const data = {
      codiceEnte: params.codiceEnte || undefined,
      tipoRegistro: params.tipoRegistro || undefined,
      idAnnoPratica: params.idAnnoPratica ? Number.parseInt(params.idAnnoPratica, 10) : undefined,
      progressivoPratica: params.progressivoPratica
        ? Number.parseInt(params.progressivoPratica, 10)
        : undefined,
      timestampSospensione: values.date ? values.date?.toFormat('yyyy-MM-dd') : undefined,
      note: values.note || undefined,
    };

    (insertData({ data }) as ResponsePromise<void>).then(async () => {
      toast.success('Operazione eseguita con successo');

      // se cambia lo stato della pratica ricaricare dati
      const newData: PracticeDetailVM | void = await loadUpdateDate();

      if (newData) {
        formContextPage.setValues({
          ...(formContextPage.values || {}),
          ...newData,
        });
      }

      loadViewMessi();
    });
  });

  const formContext = useFormik<VoidVerificationMessiVM>({
    initialValues: {
      date: DateTime.now(),
      note: '',
    },
    validationSchema: schema,
    onSubmit,
  });

  return (
    <Fragment>
      <Accordion
        expanded={open}
        onChange={() => {
          setOpen(!open);
        }}
        ref={ref}
      >
        <AccordionSummary
          aria-controls="panel-verification-void-assessment-content"
          id="panel-verification-void-assessment-header"
          expandIcon={<ExpandMore />}
        >
          <Typography>Annullo Accertamento</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <AccordionIssuedAssessmentForm
            values={formContext.values}
            onChange={formContext.setFieldValue}
            onChanges={(values) => {
              formContext.setValues({
                ...formContext.values,
                ...values,
              });
            }}
            errors={formContext.errors}
            touched={formContext.touched}
          />
          <div className="pmi-d-flex">
            <div className="pmi-align-right pmi-pt-2">
              <PmiButton
                color="cdmBrandThird"
                variant="outlined"
                onClick={() => formContext.handleSubmit()}
                id="return"
              >
                Conferma
              </PmiButton>
            </div>
          </div>
        </AccordionDetails>
      </Accordion>
    </Fragment>
  );
};

VoidVerificationMessiComponent.displayName = 'Accordion Void Verification Messi component';

const VoidVerificationMessi = memo(VoidVerificationMessiComponent);

export default VoidVerificationMessi;
